import React, { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Alert, Avatar, Badge, Dropdown } from "antd";
import { UserOutlined, MailOutlined, ClockCircleOutlined, LogoutOutlined, MenuOutlined } from "@ant-design/icons";
import { useAuth } from "@/utils/auth.jsx";
import { useTopUp } from "@/utils/useTopUp.js";
import { TopUpModal } from "@components/TopUp.jsx";
import { useLockedFeature } from "@components/LockedFeature.jsx";
import { useMobileNav } from "@/utils/MobileNavContext.jsx";
import { USER_NAV_GROUPS, ADMIN_NAV_GROUPS } from "@/assets/data/navConfig.js";

const ADMIN_UID = "U4mlColHvRdwiA0q6jU13cuMExB3";

const isGroupActive = (group, pathname) => {
  if (group.to) return pathname.startsWith(group.to);
  if (group.items) return group.items.some((item) => item.to && pathname.startsWith(item.to));
  return false;
};

const TopNav = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const topnavRef = useRef(null);

  useEffect(() => {
    const el = topnavRef.current;
    if (!el) return;

    const setHeightVar = () => {
      document.documentElement.style.setProperty("--topnav-height", `${el.offsetHeight}px`);
    };

    setHeightVar();

    const observer = new ResizeObserver(setHeightVar);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const {
    firestoreUser,
    pendingSum,
    isModalOpen,
    openModal,
    closeModal,
    handleWithdrawal,
    success,
    error,
    clearSuccess,
    clearError,
  } = useTopUp();

  const { openLocked, lockedModal } = useLockedFeature();
  const { toggleMobileNav } = useMobileNav();

  const isAdmin = user?.uid === ADMIN_UID;
  const groups = (isAdmin ? ADMIN_NAV_GROUPS : USER_NAV_GROUPS).filter((group) => group.key !== "other");

  const handleGroupClick = (event, group) => {
    if (group.locked || group.topNavLocked) {
      openLocked(event);
    }
  };

  const avatarMenu = {
    items: [
      { key: "email", label: user?.email, disabled: true },
      { type: "divider" },
      { key: "logout", label: "Log Out", icon: <LogoutOutlined />, onClick: () => logout() },
    ],
  };

  return (
    <div className="topnav" ref={topnavRef}>
      {success && (
        <Alert
          message="Withdrawal request created and pending approval"
          type="success"
          showIcon
          closable
          onClose={clearSuccess}
        />
      )}
      {error && (
        <Alert
          message="Failed to create withdrawal request!"
          type="error"
          showIcon
          closable
          onClose={clearError}
        />
      )}

      <button className="topnav-hamburger" onClick={toggleMobileNav} title="Menu">
        <MenuOutlined />
      </button>

      <div className="topnav-tabs">
        {groups.map((group) =>
          group.locked || group.topNavLocked ? (
            <a
              key={group.key}
              className="topnav-tab"
              href="#"
              onClick={(event) => handleGroupClick(event, group)}
            >
              {group.label}
            </a>
          ) : (
            <NavLink
              key={group.key}
              to={group.to || group.items?.[0]?.to || "#"}
              className={() => `topnav-tab${isGroupActive(group, location.pathname) ? " active" : ""}`}
            >
              {group.label}
            </NavLink>
          )
        )}
      </div>

      <div className="topnav-actions">
        <button className="topnav-icon" onClick={openLocked} title="Profile">
          <UserOutlined />
        </button>
        <button className="topnav-icon" onClick={openLocked} title="Messages">
          <MailOutlined />
        </button>

        {pendingSum > 0 && (
          <Badge count={<ClockCircleOutlined style={{ color: "#faad14" }} />}>
            <span className="topnav-balance" onClick={openModal}>
              $ {firestoreUser?.balance ?? 0}
            </span>
          </Badge>
        )}
        {pendingSum <= 0 && (
          <span className="topnav-balance" onClick={openModal}>
            $ {firestoreUser?.balance ?? 0}
          </span>
        )}

        <Dropdown menu={avatarMenu} trigger={["click"]} placement="bottomRight">
          <Avatar className="topnav-avatar" icon={<UserOutlined />} />
        </Dropdown>
      </div>

      <TopUpModal open={isModalOpen} onCancel={closeModal} onSubmit={handleWithdrawal} />
      {lockedModal}
    </div>
  );
};

export default TopNav;
